import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import portalApi, { portalFileUrl, formatErr } from "./portalApi";
import { useBranding } from "./branding";
import SignaturePad from "./SignaturePad";
import { ArrowLeft, Download, CheckCircle2, XCircle, Clock, AlertCircle, FileText } from "lucide-react";

const cad = (n) => `CA$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fdate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-CA", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

export default function PortalEstimateDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { branding } = useBranding();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // approval
  const [showApprove, setShowApprove] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);

  // decline
  const [showDecline, setShowDecline] = useState(false);
  const [reason, setReason] = useState("");

  const load = async () => {
    try {
      const { data } = await portalApi.get(`/portal/estimates/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (err) return (
    <div className="max-w-md mx-auto bg-white border border-red-200 rounded-md p-6 text-center">
      <AlertCircle size={28} className="mx-auto text-red-500" />
      <p className="text-sm text-slate-700 mt-3">{err}</p>
      <Link to="/portal" className="inline-block mt-4 text-sm text-[#0F4C81] underline">Back to portal</Link>
    </div>
  );
  if (!doc) return null;

  const primary = branding?.primary_color || "#0F4C81";
  const isPending = ["Sent", "Viewed"].includes(doc.status);

  const submitApprove = async (e) => {
    e.preventDefault();
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setErr("Please sign before approving");
      return;
    }
    setBusy(true); setErr("");
    try {
      await portalApi.post(`/portal/estimates/${id}/approve`, {
        name: name.trim(),
        signature_data_url: sigRef.current.toDataURL(),
      });
      setShowApprove(false);
      await load();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const submitDecline = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await portalApi.post(`/portal/estimates/${id}/decline`, { reason });
      setShowDecline(false);
      await load();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = () => {
    if (doc.status === "Approved") return <Badge color="bg-emerald-100 text-emerald-800" icon={CheckCircle2} label={`Approved · ${fdate(doc.approved_at)}`} />;
    if (doc.status === "Declined") return <Badge color="bg-slate-100 text-slate-700" icon={XCircle} label={`Declined · ${fdate(doc.declined_at)}`} />;
    if (doc.status === "Expired") return <Badge color="bg-slate-100 text-slate-500" icon={XCircle} label="Expired" />;
    return <Badge color="bg-amber-100 text-amber-800" icon={Clock} label="Awaiting your review" />;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="portal-estimate-detail">
      <Link to="/portal" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 sm:px-7 py-6 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-medium">
                <FileText size={13} /> Estimate
              </div>
              <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">{doc.number}</h1>
              <div className="mt-3">{statusBadge()}</div>
            </div>
            <a
              href={portalFileUrl(`/portal/estimates/${id}/pdf`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portal-estimate-pdf-btn"
              className="self-start px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 flex items-center gap-2"
            >
              <Download size={14} /> Download PDF
            </a>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-6 text-sm">
            <Field label="Date" value={fdate(doc.created_at)} />
            <Field label="Valid until" value={fdate(doc.expiration_date)} />
            <Field label="Total" value={<span className="tabular font-semibold">{cad(doc.total)}</span>} />
          </div>
        </div>

        <div className="px-5 sm:px-7 py-6">
          <h3 className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">Line items</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 text-right">Qty</th>
                  <th className="py-2 text-right">Rate</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(doc.line_items || []).map((li, idx) => {
                  const sub = Number(li.quantity || 0) * Number(li.rate || 0);
                  const disc = li.line_discount_type === "percent"
                    ? sub * (Number(li.line_discount_value || 0) / 100)
                    : Number(li.line_discount_value || 0);
                  const total = Math.max(sub - disc, 0);
                  return (
                    <tr key={idx} className="border-b border-slate-100 align-top">
                      <td className="py-3 pr-3">
                        <div className="font-medium text-slate-900">{li.name}</div>
                        {li.description && <div className="text-xs text-slate-500 mt-0.5">{li.description}</div>}
                      </td>
                      <td className="py-3 text-right tabular text-slate-600">{li.quantity} {li.unit}</td>
                      <td className="py-3 text-right tabular text-slate-600">{cad(li.rate)}</td>
                      <td className="py-3 text-right tabular font-medium">{cad(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-5 ml-auto max-w-xs space-y-1.5 text-sm">
            <Row label="Subtotal" value={cad(doc.subtotal)} />
            {doc.invoice_discount_amount > 0 && <Row label="Discount" value={`− ${cad(doc.invoice_discount_amount)}`} />}
            <Row label={`HST (${doc.hst_rate}%)`} value={cad(doc.hst)} />
            <div className="border-t border-slate-200 pt-2 mt-2 flex justify-between text-base">
              <span className="font-semibold text-slate-900">Total</span>
              <span className="tabular font-bold text-slate-900">{cad(doc.total)}</span>
            </div>
          </div>

          {doc.notes && (
            <div className="mt-6 text-sm text-slate-600 border-l-2 border-slate-200 pl-4 whitespace-pre-wrap">
              {doc.notes}
            </div>
          )}
        </div>

        {isPending && (
          <div className="px-5 sm:px-7 py-5 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row gap-2 justify-end">
            <button
              onClick={() => setShowDecline(true)}
              data-testid="portal-decline-btn"
              className="px-4 py-2.5 border border-slate-300 bg-white text-slate-700 text-sm rounded-md hover:bg-slate-50 font-medium"
            >
              Decline
            </button>
            <button
              onClick={() => setShowApprove(true)}
              data-testid="portal-approve-btn"
              style={{ background: primary }}
              className="px-5 py-2.5 text-white text-sm rounded-md font-medium hover:opacity-90 flex items-center justify-center gap-2"
            >
              <CheckCircle2 size={16} /> Approve & sign
            </button>
          </div>
        )}

        {doc.status === "Approved" && doc.has_signature && (
          <div className="px-5 sm:px-7 py-5 border-t border-slate-100 bg-emerald-50 text-sm text-emerald-900" data-testid="portal-approved-banner">
            <div className="font-semibold flex items-center gap-2"><CheckCircle2 size={16}/> Approved by {doc.approval_name}</div>
            <div className="text-xs mt-1">on {fdate(doc.approved_at)}</div>
          </div>
        )}
        {doc.status === "Declined" && (
          <div className="px-5 sm:px-7 py-5 border-t border-slate-100 bg-slate-50 text-sm text-slate-700" data-testid="portal-declined-banner">
            <div className="font-semibold flex items-center gap-2"><XCircle size={16}/> You declined this estimate</div>
            {doc.decline_reason && <div className="text-xs text-slate-500 mt-1">Reason: {doc.decline_reason}</div>}
          </div>
        )}
      </div>

      {/* Approve modal */}
      {showApprove && (
        <Modal onClose={() => !busy && setShowApprove(false)}>
          <form onSubmit={submitApprove} data-testid="portal-approve-form" className="space-y-4">
            <div>
              <h3 className="font-display text-xl font-bold text-slate-900">Approve estimate {doc.number}</h3>
              <p className="text-sm text-slate-500 mt-1">By signing below, you authorize the work outlined in this estimate.</p>
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Your full name</label>
              <input
                data-testid="portal-approve-name"
                type="text" required value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-slate-900"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Signature</label>
                <button type="button" onClick={() => sigRef.current?.clear()} className="text-xs text-slate-500 hover:text-slate-900 underline">Clear</button>
              </div>
              <SignaturePad ref={sigRef} />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => setShowApprove(false)} disabled={busy} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
              <button type="submit" data-testid="portal-approve-submit" disabled={busy} style={{ background: primary }} className="px-5 py-2 text-white text-sm rounded-md font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2">
                <CheckCircle2 size={14}/> {busy ? "Submitting…" : "Approve estimate"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Decline modal */}
      {showDecline && (
        <Modal onClose={() => !busy && setShowDecline(false)}>
          <form onSubmit={submitDecline} data-testid="portal-decline-form" className="space-y-4">
            <div>
              <h3 className="font-display text-xl font-bold text-slate-900">Decline estimate {doc.number}?</h3>
              <p className="text-sm text-slate-500 mt-1">Let us know why if you'd like — it helps us improve.</p>
            </div>
            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}
            <textarea
              data-testid="portal-decline-reason"
              rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Optional"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-slate-900"
            />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowDecline(false)} disabled={busy} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Keep open</button>
              <button type="submit" data-testid="portal-decline-submit" disabled={busy} className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm rounded-md font-medium disabled:opacity-60">
                {busy ? "Submitting…" : "Decline"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="mt-1 text-slate-900">{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between text-slate-600">
      <span>{label}</span>
      <span className="tabular text-slate-900">{value}</span>
    </div>
  );
}
function Badge({ color, icon: Icon, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${color}`}>
      <Icon size={12} /> {label}
    </span>
  );
}
function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center sm:items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl">
        {children}
      </div>
    </div>
  );
}
